import { PortfolioInvestmentTypeSelection } from "@models/managed-accounts/entities/step/setup";
import { AnalysisFeeCalculatorDTO } from "@models/platform-analysis/entities/steps/fee";
import { AnalysisDataValue } from "@models/platform-analysis/enums/dataValue";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { getCalculateTotals } from "@pages/platform-analysis-v2/_id/steps/fee/calculator/utils";
import { RootState } from "@redux/store";
import { useCallback, useMemo } from "react";
import { FieldPath, useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";

export const useFeeCalculator = () => {
  const formMethods = useFormContext<AnalysisFeeCalculatorDTO>();
  const { control } = formMethods;

  const pathsMap = useSelector(
    (state: RootState) =>
      state.platformAnalysis?.internal?.feeCalculator?.feeCalculatorPathsMap,
  );

  // #region export Methods
  const getDataValuePath = useCallback(
    (variableName: AnalysisVariable) => {
      return pathsMap[variableName]
        ?.dataValuePath as FieldPath<AnalysisFeeCalculatorDTO>;
    },
    [pathsMap],
  );

  const getFeeCalculatorPath = useCallback(
    (variableName: AnalysisVariable) => {
      return pathsMap[variableName]
        ?.feeCalculatorPath as FieldPath<AnalysisFeeCalculatorDTO>;
    },
    [pathsMap],
  );

  const resetFormData = useCallback(
    (data: AnalysisFeeCalculatorDTO) => {
      formMethods.reset(data);
    },
    [formMethods],
  );
  // #endregion export Methods

  // #region Watched Values
  const watchedFamilyMembers = useWatch({ control, name: "familyMembers" });
  const watchedIsShowHoldingsNumber = useWatch({
    control,
    name: "isShowHoldingsNumber",
  });
  const watchedContainsCfsEdge = useWatch({ control, name: "containsCfsEdge" });
  const watchedContainsNorth = useWatch({ control, name: "containsNorth" });
  const watchedContainsCps = useWatch({ control, name: "containsCps" });

  const watchedIsChoiceMF = useWatch({
    control,
    name: getDataValuePath(AnalysisVariable.Choose_MF),
  });
  const watchedIsChoiceAU = useWatch({
    control,
    name: getDataValuePath(AnalysisVariable.Choose_AU),
  });
  const watchedIsChoiceIntl = useWatch({
    control,
    name: getDataValuePath(AnalysisVariable.Choose_Intl),
  });
  const watchedIsChoiceMA = useWatch({
    control,
    name: getDataValuePath(AnalysisVariable.Choose_MA),
  });
  const watchedIsChoiceTD = useWatch({
    control,
    name: getDataValuePath(AnalysisVariable.Choose_TD),
  });
  const watchedIsChoiceRetIns = useWatch({
    control,
    name: getDataValuePath(AnalysisVariable.Choose_ret_ins),
  });
  const watchedIsChoiceBond = useWatch({
    control,
    name: getDataValuePath(AnalysisVariable.Choose_bond),
  });
  // #endregion Watched Values

  // #region General balances
  const calculatedTotals = useMemo(
    () => getCalculateTotals(watchedFamilyMembers),
    [watchedFamilyMembers],
  );

  const idpsBalance = useMemo(() => {
    return calculatedTotals.idps;
  }, [calculatedTotals]);

  const superBalance = useMemo(() => {
    return calculatedTotals.super;
  }, [calculatedTotals]);

  const hasIDPS = useMemo(() => {
    return idpsBalance > 0;
  }, [idpsBalance]);
  const hasSuper = useMemo(() => {
    return superBalance > 0;
  }, [superBalance]);

  const watchedIdpsCashBalance = useWatch({
    control,
    name: getDataValuePath(AnalysisVariable.IDPS_Cash_balance),
  });
  const idpsCashBalance = useMemo(() => {
    const isValid = !isNaN(Number(watchedIdpsCashBalance));

    return isValid ? Number(watchedIdpsCashBalance) : 0;
  }, [watchedIdpsCashBalance]);

  const watchedSuperCashBalance = useWatch({
    control,
    name: getDataValuePath(AnalysisVariable.Super_Cash_balance),
  });

  const superCashBalance = useMemo(() => {
    const isValid = !isNaN(Number(watchedSuperCashBalance));

    return isValid ? Number(watchedSuperCashBalance) : 0;
  }, [watchedSuperCashBalance]);

  const totalCash = useMemo(
    () => idpsCashBalance + superCashBalance,
    [idpsCashBalance, superCashBalance],
  );
  // #endregion General balances

  // #region Portfolio Level
  const portfolioLevel = useWatch({
    control,
    name: getDataValuePath(AnalysisVariable.Portfolio_setup),
  });
  const isNoPortfolioLevel =
    !portfolioLevel || portfolioLevel === FeeCalculatorPortfolioLevel.No;
  const isPortfolioFamilyLevel =
    portfolioLevel && portfolioLevel === FeeCalculatorPortfolioLevel.Family;
  // #endregion Portfolio Level

  // #region Investment option selections
  const isChooseMF = useMemo(() => {
    return watchedIsChoiceMF === AnalysisDataValue.True;
  }, [watchedIsChoiceMF]);
  const isChooseAU = useMemo(() => {
    return watchedIsChoiceAU === AnalysisDataValue.True;
  }, [watchedIsChoiceAU]);
  const isChooseIntl = useMemo(() => {
    return watchedIsChoiceIntl === AnalysisDataValue.True;
  }, [watchedIsChoiceIntl]);
  const isChooseMA = useMemo(() => {
    return watchedIsChoiceMA === AnalysisDataValue.True;
  }, [watchedIsChoiceMA]);
  const isChooseTD = useMemo(() => {
    return watchedIsChoiceTD === AnalysisDataValue.True;
  }, [watchedIsChoiceTD]);
  const isChooseRetIns = useMemo(() => {
    return watchedIsChoiceRetIns === AnalysisDataValue.True;
  }, [watchedIsChoiceRetIns]);
  const isChooseBond = useMemo(() => {
    return watchedIsChoiceBond === AnalysisDataValue.True;
  }, [watchedIsChoiceBond]);
  const investmentTypeSelections: PortfolioInvestmentTypeSelection =
    useMemo(() => {
      return {
        isChooseMF,
        isChooseAU,
        isChooseIntl,
        isChooseMA,
        isChooseTD,
        isChooseRetIns,
        isChooseBond,
      };
    }, [
      isChooseMF,
      isChooseAU,
      isChooseIntl,
      isChooseMA,
      isChooseTD,
      isChooseRetIns,
      isChooseBond,
    ]);
  // #endregion Investment option selections

  return {
    pathsMap,
    formMethods,
    portfolioLevel,
    calculatedTotals,
    totalCash,
    hasIDPS,
    hasSuper,
    isChooseMF,
    isChooseAU,
    isChooseIntl,
    isChooseMA,
    isChooseTD,
    isChooseRetIns,
    isChooseBond,
    investmentTypeSelections,
    containsCfsEdge: !!watchedContainsCfsEdge,
    containsNorth: !!watchedContainsNorth,
    containsCps: !!watchedContainsCps,
    showHoldingNumber: !!watchedIsShowHoldingsNumber,
    isNoPortfolioLevel: !!isNoPortfolioLevel,
    isPortfolioFamilyLevel: !!isPortfolioFamilyLevel,
    resetFormData,
    getDataValuePath,
    getFeeCalculatorPath,
  };
};

export type EditAnalysisFeeCalculatorReturnType = ReturnType<
  typeof useFeeCalculator
>;
